import type { ReactNode } from "react";

import { useStore } from "@/lib/store";
import type { Session } from "@/lib/types";

import { SidebarSessionItem } from "./SidebarSessionItem";

interface SidebarSessionListProps {
  sessions: Session[];
  isLoading?: boolean;
  headerAction?: ReactNode;
}

function toLocalDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getLocalDayKey(dateStr: string): string {
  return toLocalDayKey(new Date(dateStr));
}

function formatDayLabel(dayKey: string): string {
  const today = new Date();
  const todayKey = toLocalDayKey(today);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = toLocalDayKey(yesterday);

  if (dayKey === todayKey) return "Today";
  if (dayKey === yesterdayKey) return "Yesterday";

  const [year, month, day] = dayKey.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
  }).format(date);
}

export function SidebarSessionList({
  sessions,
  isLoading,
  headerAction,
}: SidebarSessionListProps): React.JSX.Element {
  const filters = useStore((state) => state.filters);
  const clearFilters = useStore((state) => state.clearFilters);
  const hasActiveFilters = Boolean(
    filters.search ||
    filters.person ||
    filters.status ||
    filters.source ||
    filters.includeAutomated ||
    !filters.mine,
  );

  const content = isLoading ? (
    <div className="text-cyber-muted flex items-center justify-center px-4 py-6 text-sm">
      <span className="animate-spin-glow mr-2 inline-block">◐</span>
      Loading...
    </div>
  ) : sessions.length === 0 ? (
    <div className="text-cyber-muted px-4 py-6 text-center text-sm">
      <div>No sessions yet</div>
      {hasActiveFilters && (
        <button
          type="button"
          onClick={clearFilters}
          className="text-cyber-cyan hover:text-cyber-cyan/80 mt-3 text-xs transition-colors"
        >
          Reset search and filters
        </button>
      )}
    </div>
  ) : (
    <div>
      {sessions.map((session, index) => {
        const dayKey = getLocalDayKey(session.lastActivity);
        const previousDayKey = index > 0 ? getLocalDayKey(sessions[index - 1].lastActivity) : null;
        const shouldRenderSeparator = index > 0 && previousDayKey !== dayKey;

        return (
          <div key={session.threadKey}>
            {shouldRenderSeparator && (
              <div className="relative z-0 flex items-center justify-between pt-3 pr-3 pb-1 pl-4">
                <div className="text-cyber-muted/65 text-[10px] font-medium tracking-[0.18em] uppercase">
                  {formatDayLabel(dayKey)}
                </div>
              </div>
            )}
            <SidebarSessionItem session={session} />
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div className="relative z-0 flex items-center justify-between pr-3 pb-1 pl-4">
        <div className="text-cyber-muted/65 text-[10px] tracking-[0.18em] uppercase">
          {!isLoading && sessions[0]
            ? formatDayLabel(getLocalDayKey(sessions[0].lastActivity))
            : "Chats"}
        </div>
        {headerAction}
      </div>
      {content}
    </div>
  );
}
